import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Hammer } from 'lucide-react'

import { extractApiError, FormModal } from '@components/branded-modal'
import { CurrencyInput } from '@components/currency-input'
import { InfoPopover } from '@components/popover'
import { AdasFormControl, AdasInputLabel, AdasSelect, AdasTextField } from '@components/wrapper-components'

import {
  saveCalibration,
  saveService,
  saveLabour,
  saveParts,
  getProductCategories,
  updateCalibration,
  updateService,
  updateParts,
  updateLabour
} from '../../../api/api'

import { useUserStore } from '../../../store'
import { useCenterStore } from '@caradasstore/CenterStore'
import { validations, formatInitialPrice } from '../../../utils/utils'
import { Logger } from '../../../logger'

const prettyTypeName = {
  calibration: 'Calibration',
  service: 'Service',
  part: 'Part',
  labor: 'Labor'
}

const profileLabel = {
  calibration: 'STORE',
  auto_quote: 'AUTO QUOTE',
  national: 'NATIONAL AGREEMENT'
}

const categoryLabel = {
  calibration: 'CALIBRATIONS',
  service: 'SERVICES',
  part: 'PARTS',
  labor: 'LABOR'
}

const saveHandlers = {
  calibration: saveCalibration,
  service: saveService,
  part: saveParts,
  labor: saveLabour
}
const updateHandlers = {
  calibration: updateCalibration,
  service: updateService,
  part: updateParts,
  labor: updateLabour
}

const SegmentedButton = ({ active, children, onClick, type = 'button', disabled = false }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`px-5 py-1.5 text-[12px] font-semibold uppercase tracking-wide rounded-md transition-colors ${
      active ? 'bg-[#E8EFFF] text-[#1F6FEB] border border-[#1F6FEB]' : 'bg-white text-[#6B7280] border border-[#D1D5D8] hover:bg-[#F8FAFC]'
    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
  >
    {children}
  </button>
)

SegmentedButton.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  type: PropTypes.string,
  disabled: PropTypes.bool
}

export const ProductForm = ({
  open = true,
  productData,
  close,
  onSaveSuccess,
  productTypeName,
  edit,
  multiline = true,
  selectedReferralId,
  selectedProfileType = 'calibration'
}) => {
  const ccId = useCenterStore((store) => store.ccId)
  const currentUser = useUserStore((store) => store.currentUser)
  const isAdmin = currentUser.is_admin
  const isOwner = currentUser.is_owner
  // Auto Quote products are generated — only price and active/inactive status are editable.
  // The category is fixed, so we show its existing value read-only and skip the category API.
  const isAutoQuote = selectedProfileType === 'auto_quote'
  const [productCategories, setProductCategories] = useState([])
  const [apiError, setApiError] = useState(null)
  const [touched, setTouched] = useState({ name: false, price: false, category: false })
  const [infoLabel] = useState(`A Default ${prettyTypeName[productTypeName]} is automatically included in all new Quotes & Work Orders`)
  const [isCategoryDisabled, setIsCategoryDisabled] = useState(false)

  const [product, setProduct] = useState({
    is_active: true,
    quantity: productData.quantity || 1,
    category_id: productData?.category?.id || '',
    ...productData
  })

  const [valid, setValid] = useState({
    name: validations.name(productData.name),
    price: validations.price(productData.price),
    category: productTypeName === 'calibration' ? validations.category(productData?.category?.id) : true
  })

  const fetchProductCategories = useCallback(async () => {
    try {
      const response = await getProductCategories({ ccid: ccId, type: productTypeName.toUpperCase() })
      if (response.status === 200) {
        const categories = response.data
        setProductCategories(categories)

        if (categories.length === 1 && productTypeName !== 'calibration') {
          const singleCategory = categories[0]
          setProduct((prev) => ({
            ...prev,
            category_id: singleCategory.id
          }))
          setValid((prev) => ({ ...prev, category: true }))
          setTouched((prev) => ({ ...prev, category: true }))
          setIsCategoryDisabled(true)
        }
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductForm/fetchProductCategories' } })
    }
  }, [ccId, productTypeName])

  useEffect(() => {
    // Auto Quote shows the existing category read-only — no need to fetch the category list.
    if (isAutoQuote) return
    fetchProductCategories()
  }, [fetchProductCategories, isAutoQuote])

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target

      if (name === 'category') {
        const selectedCategory = productCategories.find((cat) => cat.id === value)
        const validCategory = validations.category(selectedCategory?.id)

        setProduct((prev) => ({ ...prev, category_id: selectedCategory?.id || '' }))
        setTouched((prev) => ({ ...prev, category: true }))
        setValid((prev) => ({ ...prev, category: validCategory }))
        return
      }

      const sanitizedValue = name === 'quantity' ? parseInt(value.replace(/\D/g, ''), 10) || 0 : value

      setProduct((prev) => ({ ...prev, [name]: sanitizedValue }))
      setTouched((prev) => ({ ...prev, [name]: true }))
      setValid((prev) => ({
        ...prev,
        [name]: validations[name] ? validations[name](sanitizedValue) : true
      }))
    },
    [productCategories]
  )

  const handleSetActive = (isActive) => {
    setProduct((prev) => ({
      ...prev,
      is_active: isActive,
      is_defaulted: isActive ? prev.is_defaulted : false
    }))
  }

  const handleSetDefault = (isDefault) => {
    setProduct((prev) => ({
      ...prev,
      is_defaulted: isDefault
    }))
  }

  const getPayload = () => {
    const baseData = {
      ...product,
      product_type: productTypeName.toUpperCase(),
      belongsTo: selectedReferralId ? 'REFERRAL' : 'CALIBRATION_CENTER',
      belongs_to_id: selectedReferralId ? selectedReferralId : ccId,
      calibration_center_id: productData.calibration_center_id || ccId
    }
    return baseData
  }

  const handleSave = async () => {
    const actionHandlers = edit ? updateHandlers : saveHandlers
    const actionFunc = actionHandlers[productTypeName]
    if (!actionFunc) {
      Logger.error({ message: `Unknown product type: ${productTypeName}` })
      return
    }
    const data = getPayload()
    setApiError(null)
    try {
      const response = await actionFunc({ data })
      if (response.status === 200) {
        close()
        onSaveSuccess(product)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductForm/handleSave', product } })
      setApiError(extractApiError(error) || 'Failed to save. Please try again.')
    }
  }

  const isSaveDisabled = useMemo(() => !valid.name || !valid.price || !valid.category, [valid])
  const priceLabel = productTypeName === 'labor' ? 'Hourly Rate *' : 'Price *'
  const quantityLabel = productTypeName === 'labor' ? 'Hours' : 'Quantity'

  const profile = profileLabel[selectedProfileType] || 'STORE'
  const category = categoryLabel[productTypeName] || (productTypeName || '').toUpperCase()
  const productIdLabel = product.itemNum ? `#${product.itemNum}` : product.id ? `#${product.id}` : ''
  const titleAction = edit ? 'Edit' : 'Add'
  const formTitle = `${titleAction} ${prettyTypeName[productTypeName] || ''}`.trim()

  const isReadOnly = edit && !isAdmin && !isOwner
  const categoryDisabled = isCategoryDisabled || isAutoQuote || (edit && !isAdmin && productTypeName === 'calibration')

  const eyebrow = (
    <>
      {profile}
      {' · '}
      {category}
      {productIdLabel && (
        <>
          {' · '}
          {productIdLabel}
        </>
      )}
    </>
  )

  const footer = (
    <div className='flex items-center justify-between gap-3'>
      <button
        type='button'
        onClick={close}
        className='inline-flex items-center justify-center rounded-md border border-[#D1D5D8] px-5 py-2 text-[13px] font-semibold uppercase tracking-wide text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
      >
        Cancel
      </button>
      <button
        type='submit'
        onClick={handleSave}
        disabled={isSaveDisabled}
        className='inline-flex items-center justify-center rounded-md bg-[#0A1628] px-5 py-2 text-[13px] font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-50'
      >
        Save Changes
      </button>
    </div>
  )

  return (
    <FormModal
      open={open}
      onClose={close}
      title={formTitle}
      icon={Hammer}
      eyebrow={eyebrow}
      footer={footer}
      errorMessage={apiError}
      onDismissError={() => setApiError(null)}
    >
      <div>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          {isAutoQuote ? (
            <AdasTextField
              name='category'
              id='category'
              label='Category'
              variant='outlined'
              value={product.category?.name || ''}
              disabled
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <AdasFormControl fullWidth>
              <AdasInputLabel id='category_label' shrink>
                Category
              </AdasInputLabel>
              <AdasSelect
                labelId='Category'
                id='category'
                name='category'
                value={product.category_id}
                label='Category'
                onChange={handleInputChange}
                disabled={categoryDisabled}
                options={productCategories}
                error={touched.category && !valid.category}
              />
            </AdasFormControl>
          )}

          <AdasTextField
            onChange={handleInputChange}
            name='quantity'
            id='quantity'
            label={quantityLabel}
            variant='outlined'
            value={product.quantity || ''}
            disabled
            fullWidth
            inputProps={{ maxLength: 3, inputMode: 'numeric', pattern: '\\d*' }}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div className='mt-4'>
          <AdasTextField
            onChange={handleInputChange}
            disabled={isReadOnly || isAutoQuote}
            name='name'
            id='name'
            label='Name *'
            variant='outlined'
            value={product.name || ''}
            error={!valid.name && touched.name}
            fullWidth
            inputProps={{ maxLength: 100 }}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div className='mt-4'>
          <CurrencyInput
            value={formatInitialPrice(product.price)}
            error={touched.price && !valid.price}
            label={priceLabel}
            id='price'
            name='price'
            onChange={handleInputChange}
            fullWidth
            variant='outlined'
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div className='mt-4'>
          <AdasTextField
            onChange={handleInputChange}
            disabled={isReadOnly || isAutoQuote}
            name='description'
            id='description'
            label='Description'
            variant='outlined'
            multiline={multiline}
            minRows={3}
            value={product.description || ''}
            fullWidth
            inputProps={{ maxLength: 250 }}
            InputLabelProps={{ shrink: true }}
            helperText='Optional — customer-facing description'
          />
        </div>

        {/* Segmented controls */}
        <div className='mt-5 flex flex-col sm:flex-row gap-6'>
          <div className='flex flex-col gap-2'>
            <span className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Status</span>
            <div className='inline-flex items-center gap-2'>
              <SegmentedButton active={!!product.is_active} onClick={() => handleSetActive(true)}>
                Active
              </SegmentedButton>
              <SegmentedButton active={!product.is_active} onClick={() => handleSetActive(false)}>
                Inactive
              </SegmentedButton>
            </div>
          </div>

          <div className='flex flex-col gap-2'>
            <span className='inline-flex items-center gap-1 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>
              Default {prettyTypeName[productTypeName]}
              <InfoPopover text={infoLabel} ariaLabel='Information on default' className='p-0.5' />
            </span>
            <div className='inline-flex items-center gap-2'>
              <SegmentedButton active={!!product.is_defaulted} onClick={() => handleSetDefault(true)} disabled={isAutoQuote}>
                Yes
              </SegmentedButton>
              <SegmentedButton active={!product.is_defaulted} onClick={() => handleSetDefault(false)} disabled={isAutoQuote}>
                No
              </SegmentedButton>
            </div>
          </div>
        </div>
      </div>
    </FormModal>
  )
}

ProductForm.propTypes = {
  open: PropTypes.bool,
  productData: PropTypes.object.isRequired,
  close: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  productTypeName: PropTypes.string.isRequired,
  edit: PropTypes.bool,
  multiline: PropTypes.bool,
  selectedReferralId: PropTypes.string,
  selectedProfileType: PropTypes.string
}
